import { CloseOutlined } from '@ant-design/icons';
import { Tooltip } from '@mm/components';
import {
  LeadsSearchFriendlyName,
  LeadsSearchQuery,
} from '@mm/state/interfaces/leads';
import { Tag } from 'antd';
import { isEqual, uniqueId } from 'lodash';
import React from 'react';
import { YES_VALUE } from '../LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { LOCATION_FIELDS } from '../LeadsSearchFiltersMenu/LocationInputFilter/LocationInputFilter';
import styles from './LeadsSearchFilterBar.module.css';

type Tag = {
  key: string;
  friendlyName: string;
  type: 'include' | 'exclude';
};

export const LeadsSearchFilterBar = ({
  filterParams,
  setFilterParams,
}: {
  filterParams: LeadsSearchQuery;
  setFilterParams: (params: LeadsSearchQuery) => void;
}) => {
  const hasFilters = filterParams.query.length;

  const mappedQueryToTags: Tag[] = filterParams.query.map(item =>
    item[1] === YES_VALUE
      ? {
          key: item[0],
          friendlyName: LeadsSearchFriendlyName[item[0]],
          type: 'include',
        }
      : { key: item[0], friendlyName: item[1], type: 'include' },
  );

  const mappedExludeToTags: Tag[] | undefined = filterParams?.exclude?.map(
    item => {
      return {
        key: item[0],
        friendlyName: item[1],
        type: 'exclude',
      };
    },
  );

  const tags = mappedExludeToTags
    ? [...mappedQueryToTags, ...mappedExludeToTags]
    : mappedQueryToTags;

  const getTooltip = (tag: Tag) => {
    const array =
      tag.type === 'include' ? filterParams.query : filterParams.exclude;
    const remappedTag: [string, string] = [tag.key, tag.friendlyName];

    const queryPair = array?.filter(
      item => item[1] !== YES_VALUE && isEqual(item, remappedTag),
    );
    if (queryPair?.length) {
      return queryPair.map(item => item && [LeadsSearchFriendlyName[item[0]]]);
    }
  };

  const getUpdatedFilterParams = (tag: Tag) => {
    const array =
      tag.type === 'include' ? filterParams.query : filterParams.exclude;
    const remappedTag: [string, string] = [tag.key, tag.friendlyName];

    return array?.filter(item =>
      item[1] === YES_VALUE
        ? !isEqual(item[0], remappedTag[0])
        : !isEqual(item, remappedTag),
    );
  };

  const onTagRemove = (tag: Tag) => {
    const updatedQuery = getUpdatedFilterParams(tag) || [];
    const updatedExlude = getUpdatedFilterParams(tag);

    const isLastLocationTagInQuery =
      filterParams.query.filter(param => LOCATION_FIELDS.includes(param[0]))
        .length === 1;
    const hasExcludedLocations = filterParams.exclude?.some(param =>
      LOCATION_FIELDS.includes(param[0]),
    );

    const removeExcludedLocations =
      isLastLocationTagInQuery &&
      LOCATION_FIELDS.includes(tag?.key) &&
      hasExcludedLocations
        ? filterParams.exclude?.filter(
            param => !LOCATION_FIELDS.includes(param[0]),
          )
        : filterParams.exclude;

    const updatedFilterParams =
      tag.type === 'include'
        ? { query: updatedQuery, exclude: removeExcludedLocations, offset: 0 }
        : { query: filterParams.query, exclude: updatedExlude, offset: 0 };
    setFilterParams(updatedFilterParams);
  };

  const onResetAll = () => {
    setFilterParams({ query: [], exclude: [], offset: 0 });
  };

  if (!filterParams.query.length && !filterParams.exclude?.length) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {Boolean(hasFilters) && (
          <Tag
            color="default"
            closable
            closeIcon={<CloseOutlined className={styles.closeIcon} />}
            onClose={onResetAll}
            onClick={onResetAll}
            className={styles.resetAll}
          >
            Reset All
          </Tag>
        )}
        {tags.map((tag: Tag) => {
          const key = `${uniqueId(tag.friendlyName)}`;
          return (
            <Tooltip
              title={getTooltip(tag)}
              key={key}
              overlayClassName={styles.tooltip}
            >
              <Tag
                key={key}
                color="geekblue"
                className={styles.tag}
                closable
                onClose={() => onTagRemove(tag)}
              >
                {LOCATION_FIELDS.includes(tag.key) && (
                  <span className={styles.fatTag}>{tag.type}:</span>
                )}
                {tag.friendlyName}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
